/**
 * Client-facing names for the budget tiers.
 *
 * The tier IDS stay `lean` / `recommended` / `aggressive` — they are the keys
 * in `config/knowledge.json`'s `tierMultipliers`, the `activeTierId` stored on
 * every proposal, the `budget_summary_<tierId>` section ids, and what the
 * agent's tools speak. Only the NAME the client reads changes, so renaming
 * never invalidates a stored proposal.
 */
const TIER_DISPLAY_NAMES: Record<string, string> = {
  lean: 'Essential',
  recommended: 'Growth',
  aggressive: 'Accelerate',
};

/** Title Case fallback for a custom tier id, e.g. 'year_two' -> 'Year Two'. */
function humanize(tierId: string): string {
  return tierId
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word[0]!.toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The name shown on the investment slides and anywhere a tier is offered to
 * the client: Essential / Growth / Accelerate (previously Good / Better /
 * Best, and before that Lean / Recommended / Aggressive). Resolved from the
 * tier ID, never from a stored `PlanTier.label`, so a proposal whose tiers
 * were generated under any of the older names displays the current ones
 * without regenerating anything.
 */
export function tierDisplayName(tierId: string): string {
  return TIER_DISPLAY_NAMES[tierId] ?? humanize(tierId);
}
